/**
 * Responsibility: describe the glacial lakeland biome — bumpy, bare ground pitted with small lakes.
 *
 * One of three biomes that hold basin lakes. All three share `lakeBasins.js` and differ only in how the land between the
 * water is shaped:
 *
 * - **this one**, bumpy and bare, which reads as scoured rock
 * - `rollingLakes`, gentle and bare, quiet lowland
 * - `woodedLakes`, gentle and wooded
 *
 * Each pair therefore differs in exactly one way, which is what makes the distinction legible from the air. Trees are
 * deliberately absent here: on ground this broken they would look like they were growing out of scree.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLakeTerrain } from '../lakeBasins.js';
import { createWaterSurface, createWaterClearance } from '../waterSurface.js';
import { LAKELAND, PALETTE } from '../../config.js';

export const lakeland = {
  id: 'lakeland',
  label: 'Glacial lakeland',

  palette: PALETTE.lakeland,

  suitabilityBias: LAKELAND.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    return createLakeTerrain(seed, 'lakeland', LAKELAND).elevation;
  },

  /** Highest elevation this biome can produce, in world units. Basins only cut down. */
  maxElevation: LAKELAND.BASE_HEIGHT + LAKELAND.BASE_RELIEF + LAKELAND.GROUND_RELIEF,

  /**
   * Build the structure placement function: the lake surfaces.
   *
   * Water is drawn in a fixed gradient by its structure kind rather than from this biome's palette, so nothing here has to
   * declare one.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const terrain = createLakeTerrain(seed, 'lakeland', LAKELAND);

    return createWaterSurface({
      ownDepthAt: terrain.ownDepth,

      // Flat by construction: the base field only, with none of this biome's bumpy ground detail in it.
      waterLevelAt: terrain.waterLevel,
      couldHoldWater: terrain.insideBasin,
      grid: LAKELAND.WATER_GRID,
      maxDepth: LAKELAND.DEPTH - LAKELAND.WATER_DROP,
    });
  },

  /**
   * Publish the lakes so nothing stands in them.
   *
   * One line, and it keeps trees, towers, rock columns and farm buildings out of the water without a change anywhere else,
   * because they all already consult the cross-biome reservation.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    const terrain = createLakeTerrain(seed, 'lakeland', LAKELAND);

    return createWaterClearance({
      ownDepthAt: terrain.ownDepth,
      nearWater: terrain.nearBasin,
    });
  },
};
